import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import type {
  GuardianToldBy, IncidentOutcome, IncidentResponse, IncidentSeverity, IncidentType,
} from '../api/types';
import { useApi } from '../auth/ApiProvider';
import { Permissions } from '../auth/permissions';
import { useSession } from '../auth/SessionProvider';
import { atTimeOfDay, timeOfDay, todayIso } from '../format';
import { usePrintedFile } from '../hooks/usePrintedFile';
import { useI18n } from '../i18n/I18nProvider';
import { DateInput, Field, Input, Select, Textarea, invalidDateIn } from './Field';
import { Badge, Button, Card, Notice, Row, Stack } from './primitives';
import { ErrorState, LoadingState } from './states';

const TYPES: IncidentType[] = ['Fall', 'Bump', 'Bite', 'Illness', 'Behavioural', 'Other'];
const SEVERITIES: IncidentSeverity[] = ['Minor', 'Moderate', 'Serious'];
const OUTCOMES: IncidentOutcome[] = ['ResolvedOnSite', 'SeenByDoctor', 'TakenToHospital', 'TakenHomeByGuardian'];
const TOLD_BY: GuardianToldBy[] = ['PhoneCall', 'InPerson'];

/** The tone a severity is drawn in, the same on every list and record. */
export function severityTone(severity: IncidentSeverity): 'danger' | 'warning' | 'neutral' {
  return severity === 'Serious' ? 'danger' : severity === 'Moderate' ? 'warning' : 'neutral';
}

/** Every list an incident appears on, so recording, acknowledging or closing one refreshes them all. */
function invalidateIncidents(queryClient: ReturnType<typeof useQueryClient>): Promise<void> {
  return queryClient.invalidateQueries({ queryKey: ['incidents'] });
}

/**
 * Records an incident for one child (FR-CHD-11), as staff in the staff app and the administration in the console.
 *
 * Every field FR-CHD-11 names: when, the type, where, what happened, the body area, what was done, first aid, and an
 * optional witness among the staff, offered to whoever may list the staff. The reporter is whoever is signed in. The
 * child's guardians are told at once by the API, and that notification cannot be switched off (FR-CHD-12).
 */
export function IncidentForm({ childId, onRecorded, onCancel }: {
  childId: number;
  onRecorded: (incident: IncidentResponse) => void;
  onCancel?: () => void;
}) {
  const api = useApi();
  const queryClient = useQueryClient();
  const { t, settings } = useI18n();
  const { can } = useSession();
  const today = todayIso(settings);

  const [date, setDate] = useState(today);
  const [time, setTime] = useState(timeOfDay(new Date(), settings));
  const [incidentType, setIncidentType] = useState<IncidentType>('Fall');
  const [severity, setSeverity] = useState<IncidentSeverity>('Minor');
  const [location, setLocation] = useState('');
  const [description, setDescription] = useState('');
  const [bodyArea, setBodyArea] = useState('');
  const [actionTaken, setActionTaken] = useState('');
  const [firstAidGiven, setFirstAidGiven] = useState(false);
  const [firstAidDetail, setFirstAidDetail] = useState('');
  const [witness, setWitness] = useState<number | ''>('');

  const mayListStaff = can(Permissions.staff.view);
  const staff = useQuery({
    queryKey: ['staff', 'active'],
    queryFn: () => api.staff.list({ status: 'Active' }),
    enabled: mayListStaff,
  });

  const occurredAt = date && time ? atTimeOfDay(date, time, settings) : null;
  const inFuture = occurredAt !== null && new Date(occurredAt).getTime() > Date.now();

  const record = useMutation({
    mutationFn: () => api.health.createIncident({
      childId,
      occurredAt,
      incidentType,
      severity,
      location: location.trim() || null,
      description: description.trim(),
      bodyArea: bodyArea.trim() || null,
      actionTaken: actionTaken.trim() || null,
      firstAidGiven,
      firstAidDetail: firstAidGiven ? firstAidDetail.trim() || null : null,
      witnessMembershipId: witness === '' ? null : witness,
    }),
    onSuccess: async (incident) => {
      await invalidateIncidents(queryClient);
      onRecorded(incident);
    },
  });

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (invalidDateIn(event.currentTarget) || inFuture || !description.trim()) return;
    record.mutate();
  };

  return (
    <Card title={t('incidents.new')} hint={t('incidents.newHint')}>
      <form noValidate onSubmit={submit}>
        <Stack>
          <Row className="dg-row--wrap">
            <Field label={t('incidents.date')}>
              {(props) => <DateInput {...props} value={date} max={today} onChange={setDate} required />}
            </Field>
            <Field label={t('care.time')}>
              {(props) => <Input {...props} type="time" value={time} onChange={(event) => setTime(event.target.value)} required />}
            </Field>
          </Row>
          {inFuture && <Notice tone="warning">{t('care.time.future')}</Notice>}

          <Row className="dg-row--wrap">
            <Field label={t('incidents.type')}>
              {(props) => (
                <Select {...props} value={incidentType} onChange={(event) => setIncidentType(event.target.value as IncidentType)}>
                  {TYPES.map((type) => <option key={type} value={type}>{t(`incidents.type.${type}`)}</option>)}
                </Select>
              )}
            </Field>
            <Field label={t('incidents.severity')}>
              {(props) => (
                <Select {...props} value={severity} onChange={(event) => setSeverity(event.target.value as IncidentSeverity)}>
                  {SEVERITIES.map((value) => <option key={value} value={value}>{t(`incidents.severity.${value}`)}</option>)}
                </Select>
              )}
            </Field>
          </Row>

          <Field label={t('incidents.description')}>
            {(props) => (
              <Textarea {...props} rows={3} required value={description} onChange={(event) => setDescription(event.target.value)} />
            )}
          </Field>
          <Field label={t('incidents.location')}>
            {(props) => <Input {...props} value={location} onChange={(event) => setLocation(event.target.value)} />}
          </Field>
          <Field label={t('incidents.bodyArea')}>
            {(props) => <Input {...props} value={bodyArea} onChange={(event) => setBodyArea(event.target.value)} />}
          </Field>
          <Field label={t('incidents.actionTaken')}>
            {(props) => (
              <Textarea {...props} rows={2} value={actionTaken} onChange={(event) => setActionTaken(event.target.value)} />
            )}
          </Field>

          <label className="dg-checkbox">
            <input type="checkbox" checked={firstAidGiven} onChange={(event) => setFirstAidGiven(event.target.checked)} />
            <span>{t('incidents.firstAid')}</span>
          </label>
          {firstAidGiven && (
            <Field label={t('incidents.firstAidDetail')}>
              {(props) => <Input {...props} value={firstAidDetail} onChange={(event) => setFirstAidDetail(event.target.value)} />}
            </Field>
          )}

          {mayListStaff && (
            <Field label={t('incidents.witness')}>
              {(props) => (
                <Select {...props} value={witness} onChange={(event) => setWitness(event.target.value === '' ? '' : Number(event.target.value))}>
                  <option value="">{t('incidents.noWitness')}</option>
                  {(staff.data ?? []).map((member) => (
                    <option key={member.membershipId} value={member.membershipId}>{member.fullName}</option>
                  ))}
                </Select>
              )}
            </Field>
          )}

          {record.isError && <ErrorState error={record.error} />}

          <Row>
            <Button type="submit" busy={record.isPending} busyLabel={t('action.saving')} disabled={!description.trim() || inFuture}>
              {t('incidents.record')}
            </Button>
            {onCancel && <Button variant="ghost" onClick={onCancel}>{t('action.cancel')}</Button>}
          </Row>
        </Stack>
      </form>
    </Card>
  );
}

/**
 * One incident in full (FR-CHD-11 to FR-CHD-14): everything recorded, who reported and witnessed it, whether the family
 * has acknowledged it and whether it is closed.
 *
 * A guardian acknowledges it here (FR-CHD-12); staff holding `incident.close` close it, and holders of `health.view`
 * print it for the nursery's file (Q16, Q41). Which of these a person is offered follows what they are, and the API
 * decides each again.
 */
export function IncidentRecord({ incidentId, as }: { incidentId: number; as: 'guardian' | 'staff' }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const { t, formatDateTime } = useI18n();
  const { can } = useSession();
  const printed = usePrintedFile();
  const [closing, setClosing] = useState(false);

  const incident = useQuery({
    queryKey: ['incidents', 'one', incidentId],
    queryFn: () => api.health.incident(incidentId),
  });

  const acknowledge = useMutation({
    mutationFn: () => api.health.acknowledgeIncident(incidentId),
    onSuccess: () => invalidateIncidents(queryClient),
  });

  if (incident.isPending) return <LoadingState />;
  if (incident.isError) return <ErrorState error={incident.error} onRetry={() => void incident.refetch()} />;

  const record = incident.data;

  const line = (label: string, value: string | null | undefined) => value ? (
    <div className="dg-stack dg-stack--tight">
      <span className="dg-muted">{label}</span>
      <span>{value}</span>
    </div>
  ) : null;

  return (
    <Card
      title={t('incidents.of', { name: record.childName })}
      action={<Badge tone={severityTone(record.severity)}>{t(`incidents.severity.${record.severity}`)}</Badge>}
    >
      <Stack>
        {line(t('incidents.when'), formatDateTime(record.occurredAt))}
        {line(t('incidents.type'), t(`incidents.type.${record.incidentType}`))}
        {line(t('incidents.description'), record.description)}
        {line(t('incidents.location'), record.location)}
        {line(t('incidents.bodyArea'), record.bodyArea)}
        {line(t('incidents.actionTaken'), record.actionTaken)}
        {record.firstAidGiven && line(t('incidents.firstAid'), record.firstAidDetail ?? t('incidents.firstAidYes'))}
        {line(t('incidents.reportedBy'), record.reportedByName)}
        {line(t('incidents.witness'), record.witnessName)}

        {record.guardianAcknowledgedAt
          ? <Notice tone="success">{t('incidents.acknowledged', { date: formatDateTime(record.guardianAcknowledgedAt) })}</Notice>
          : as === 'staff' && <Notice tone="warning">{t('incidents.awaiting')}</Notice>}
        {record.closedAt && <Notice tone="info">{t('incidents.closed', { date: formatDateTime(record.closedAt) })}</Notice>}
        {record.outcome && line(t('incidents.outcome'), t(`incidents.outcome.${record.outcome}`))}
        {line(t('incidents.followUp'), record.followUp)}
        {/* The API leaves these two out for a guardian; a staff screen shows them when they are there. */}
        {line(t('incidents.prevention'), record.prevention)}
        {record.guardianToldBy && line(t('incidents.toldBy'), t(`incidents.toldBy.${record.guardianToldBy}`))}

        {acknowledge.isError && <ErrorState error={acknowledge.error} />}
        {printed.error != null && <ErrorState error={printed.error} />}

        {closing && (
          <CloseIncidentForm
            incidentId={record.id}
            acknowledged={record.guardianAcknowledgedAt !== null}
            onDone={() => setClosing(false)}
          />
        )}

        <Row className="dg-row--wrap">
          {as === 'guardian' && !record.guardianAcknowledgedAt && (
            <Button busy={acknowledge.isPending} onClick={() => acknowledge.mutate()}>{t('incidents.acknowledge')}</Button>
          )}

          {as === 'staff' && !record.closedAt && !closing && can(Permissions.health.closeIncident) && (
            <Button variant="secondary" onClick={() => setClosing(true)}>{t('incidents.close')}</Button>
          )}

          {as === 'staff' && can(Permissions.health.view) && (
            <Button
              variant="ghost"
              disabled={printed.busy !== null}
              onClick={() => void printed.open(`incident-${record.id}`, () => api.health.incidentPdf(record.id))}
            >
              {t(printed.busy !== null ? 'action.printing' : 'action.print')}
            </Button>
          )}
        </Row>
      </Stack>
    </Card>
  );
}

/**
 * Closing an incident (FR-CHD-13): how it ended and what happened next, which the guardian reads, and optionally what
 * the nursery changed, which only staff read. Where no guardian acknowledged it in the app, how they were told too.
 * Closing is one way, so the form is the confirmation.
 */
function CloseIncidentForm({ incidentId, acknowledged, onDone }: {
  incidentId: number; acknowledged: boolean; onDone: () => void;
}) {
  const api = useApi();
  const queryClient = useQueryClient();
  const { t } = useI18n();
  const [outcome, setOutcome] = useState<IncidentOutcome>('ResolvedOnSite');
  const [followUp, setFollowUp] = useState('');
  const [prevention, setPrevention] = useState('');
  const [toldBy, setToldBy] = useState<GuardianToldBy | ''>('');

  const close = useMutation({
    mutationFn: () => api.health.closeIncident(incidentId, {
      outcome,
      followUp: followUp.trim(),
      prevention: prevention.trim() || null,
      guardianToldBy: acknowledged || toldBy === '' ? null : toldBy,
    }),
    onSuccess: async () => {
      onDone();
      await invalidateIncidents(queryClient);
    },
  });

  const ready = followUp.trim() !== '' && (acknowledged || toldBy !== '');

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (ready) close.mutate();
  };

  return (
    <form onSubmit={submit} className="dg-stack">
      <Field label={t('incidents.outcome')} required>
        {(props) => (
          <Select {...props} value={outcome} onChange={(event) => setOutcome(event.target.value as IncidentOutcome)}>
            {OUTCOMES.map((value) => <option key={value} value={value}>{t(`incidents.outcome.${value}`)}</option>)}
          </Select>
        )}
      </Field>
      <Field label={t('incidents.followUp')} hint={t('incidents.followUp.hint')} required>
        {(props) => <Textarea {...props} rows={3} value={followUp} onChange={(event) => setFollowUp(event.target.value)} />}
      </Field>
      <Field label={t('incidents.prevention')} hint={t('incidents.prevention.hint')}>
        {(props) => <Textarea {...props} rows={2} value={prevention} onChange={(event) => setPrevention(event.target.value)} />}
      </Field>
      {!acknowledged && (
        <Field label={t('incidents.toldBy')} hint={t('incidents.toldBy.hint')} required>
          {(props) => (
            <Select {...props} value={toldBy} onChange={(event) => setToldBy(event.target.value as GuardianToldBy | '')}>
              <option value="">—</option>
              {TOLD_BY.map((value) => <option key={value} value={value}>{t(`incidents.toldBy.${value}`)}</option>)}
            </Select>
          )}
        </Field>
      )}
      {close.isError && <ErrorState error={close.error} />}
      <Row>
        <Button type="submit" variant="danger" disabled={!ready} busy={close.isPending}>{t('incidents.confirmClose')}</Button>
        <Button variant="ghost" onClick={onDone}>{t('action.cancel')}</Button>
      </Row>
    </form>
  );
}

/** A child's incidents, the latest first, each opening its record (FR-CHD-14). */
export function IncidentHistory({ childId, link }: { childId: number; link: (incident: IncidentResponse) => string }) {
  const api = useApi();
  const { t, formatDateTime } = useI18n();

  const incidents = useQuery({
    queryKey: ['incidents', 'child', childId],
    queryFn: () => api.health.incidents({ childId, pageSize: 50 }),
  });

  if (incidents.isPending) return <LoadingState />;
  if (incidents.isError) return <ErrorState error={incidents.error} onRetry={() => void incidents.refetch()} />;

  const items = incidents.data.items;
  if (items.length === 0) return <p className="dg-muted">{t('incidents.empty')}</p>;

  return (
    <Stack>
      {items.map((incident) => (
        <Link key={incident.id} className="dg-child-card" to={link(incident)}>
          <span className="dg-child-card__body">
            <span className="dg-child-card__name">{t(`incidents.type.${incident.incidentType}`)}</span>
            <span className="dg-child-card__meta">
              {formatDateTime(incident.occurredAt)}
              {' '}
              <Badge tone={severityTone(incident.severity)}>{t(`incidents.severity.${incident.severity}`)}</Badge>
              {!incident.guardianAcknowledgedAt && <> <Badge tone="warning">{t('incidents.unread')}</Badge></>}
            </span>
          </span>
        </Link>
      ))}
    </Stack>
  );
}
